import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { useGetOnboardingLink } from "../hooks/useApi";
import IdCheckScreen from "../components/IdCheckScreen";
import { config } from "../config/environment";

const IdCheckPage: React.FC = () => {
  const navigate = useNavigate();
  const { exid, setTimestamp } = useAppContext();

  // Hook pour récupérer le lien d'onboarding
  const { data: onboardingData, isLoading, error } = useGetOnboardingLink(exid);

  useEffect(() => {
    if (onboardingData?.status === "ok") {
      window.location.href = config.SUCCESS_REDIRECT_URL;
    }
  }, [onboardingData]);

  // Redirection automatique si le statut est en erreur
  useEffect(() => {
    if (onboardingData && onboardingData.error) {
      // Délai de 2 secondes pour permettre à l'utilisateur de voir le résultat
      const redirectTimer = setTimeout(() => {
        window.location.href = config.REDIRECT_URL;
      }, 2000);

      return () => clearTimeout(redirectTimer);
    }
  }, [onboardingData]);

  const handleIdCheckComplete = (
    completedExid: string,
    completedTimestamp: string
  ) => {
    if (config.DEBUG)
      console.log("IdCheck terminé, navigation vers l'écran de résultat");
    setTimestamp(completedTimestamp);
    navigate("/result");
  };

  // Gestion des erreurs
  if (error) {
    return (
      <div className="error-screen">
        <h2>Erreur</h2>
        <p style={{ color: "white" }}>
          Une erreur s'est produite:{" "}
          {error instanceof Error ? error.message : "Erreur inconnue"}
        </p>
      </div>
    );
  }

  // Affichage du chargement
  if (isLoading) {
    return (
      <div className="loading-screen">
        <h2>Chargement...</h2>
        <p>Préparation de la vérification d'identité</p>
      </div>
    );
  }

  // Vérification que nous avons les données nécessaires
  if (!onboardingData || !exid) {
    return (
      <div className="error-screen">
        <h2>Données manquantes</h2>
        <p>Impossible de charger les données de vérification</p>
      </div>
    );
  }

  switch (onboardingData?.error?.code) {
    case "user_not_found":
      return (
        <div className="error-screen">
          <h2>Erreur</h2>
          <p>L'utilisateur ne semble pas exister, veuillez réessayer.</p>
        </div>
      );
    case "user_name_not_found":
      return (
        <div className="error-screen">
          <h2>Erreur</h2>
          <p>
            Le nom de famille ne semble pas correct, mettez à jour votre nom de
            famille dans votre compte.
          </p>
        </div>
      );
    case "no_valid_license":
      return (
        <div className="error-screen">
          <h2>Erreur</h2>
          <p>
            La pièce d'identité ne semble pas respecter la limite d'âge requise
            pour cette offre ou à déjà été utilisée.
          </p>
        </div>
      );
  }

  const onboardingUrl = onboardingData.url || onboardingData.onboardingUrl;
  const timestamp = onboardingData.timestamp;

  return (
    <IdCheckScreen
      onboardingUrl={onboardingUrl}
      exid={exid}
      timestamp={timestamp}
      onIdCheckComplete={handleIdCheckComplete}
    />
  );
};

export default IdCheckPage;
